import React from 'react'

import { Field } from '../../components/Field'
import { Input } from '../../components/Input'
import { ModalProvider, useModal } from '../../components/Modal'
import { Selections } from '../../components/Selections'
import { useExtractSpaceProps } from '../../hooks'
import { Arrow } from '../../icons'
import { SpaceProps } from '../../themes'
import {
  CheckboxGroupModal,
  CheckboxGroupModalProps,
} from '../CheckboxGroupModal'

export interface CheckboxGroupModalFieldProps<
  P extends string | number,
  C extends string | number,
> extends Omit<
      CheckboxGroupModalProps<P, C>,
      'title' | 'initialDate' | 'onSubmit'
    >,
    SpaceProps {
  /**
   * Input ID
   */
  id?: string

  /**
   * Label and modal title
   */
  label: string

  /**
   * Value
   */
  value?: CheckboxGroupModalProps<P, C>['initialValues']

  /**
   * Icon to display before the error message
   */
  errorIcon?: React.ReactNode

  /**
   * Error messages
   */
  errorMessages?: string | string[]

  /**
   * Icon to display before the caption message
   */
  captionIcon?: React.ReactNode

  /**
   * Caption
   */
  captionMessages?: string | string[]

  /**
   * onChange handler
   */
  onChange?: CheckboxGroupModalProps<P, C>['onSubmit']
}

const InnerCheckboxGroupModalField = <
  P extends string | number,
  C extends string | number,
>({
  id,
  value,
  label,
  options,
  errorIcon,
  errorMessages,
  captionIcon,
  captionMessages,
  onChange,
  expandOnSmallDevices,
  ...rest
}: CheckboxGroupModalFieldProps<P, C>) => {
  const autoId = React.useId()
  const inputId = id ?? autoId
  const { openModal } = useModal()
  const [spaceProps, modalProps] = useExtractSpaceProps(rest)

  const openCheckboxGroupModal = () => {
    openModal(CheckboxGroupModal<P, C>, {
      ...modalProps,
      title: label,
      options,
      expandOnSmallDevices: expandOnSmallDevices ?? true,
      initialValues: value,
      onSubmit: (value) => {
        onChange?.(value)
      },
    })
  }

  const findParent = (value: P) => {
    return options.find((o) => o.value === value)
  }

  const findChild = (value: C) => {
    return options
      .find((o) => o.children?.map(({ value }) => value).includes(value))
      ?.children?.find((c) => c.value === value)
  }

  const { parentValues = [], childValues = [] } = value ?? {}
  const selected =
    parentValues.filter((v) => findParent(v)).length > 0 ||
    childValues.filter((v) => findChild(v)).length > 0

  return (
    <Field {...spaceProps}>
      <Field.Label id={inputId}>{label}</Field.Label>
      <Field.Content mt={selected ? 3 : 0}>
        {!selected ? (
          <Input
            variant="button"
            placeholder="選択する"
            onClick={openCheckboxGroupModal}
            iconAfter={<Arrow direction="down" />}
          />
        ) : (
          <Selections
            value={
              // Flatten values
              [
                ...parentValues.map((v) => ({
                  id: `parent-${v}`,
                  label: findParent(v)?.label ?? '',
                })),
                ...childValues.map((v) => ({
                  id: `child-${v}`,
                  label: findChild(v)?.label ?? '',
                })),
              ].filter((v) => v.label !== '')
            }
            onClick={openCheckboxGroupModal}
            onClear={(id) => {
              const isParent = id.startsWith('parent-')

              onChange?.(
                isParent
                  ? {
                      parentValues:
                        parentValues.filter(
                          (v) => v !== Number(id.replace('parent-', '')),
                        ) ?? [],
                      childValues: childValues,
                    }
                  : {
                      parentValues: parentValues,
                      childValues:
                        childValues?.filter(
                          (v) => v !== Number(id.replace('child-', '')),
                        ) ?? [],
                    },
              )
            }}
          />
        )}
      </Field.Content>

      <Field.Messages
        mt={2}
        type="error"
        messages={errorMessages}
        icon={errorIcon}
      />

      <Field.Messages
        mt={errorMessages || (errorMessages && errorMessages.length) ? 1 : 2}
        type="caption"
        messages={captionMessages}
        icon={captionIcon}
      />
    </Field>
  )
}

export const CheckboxGroupModalField = <
  P extends string | number,
  C extends string | number,
>(
  props: CheckboxGroupModalFieldProps<P, C>,
) => {
  return (
    <ModalProvider>
      <InnerCheckboxGroupModalField {...props} />
    </ModalProvider>
  )
}
